// Central registry of article metadata. Used by each article page and by
// ArticleLayout to build bylines, JSON-LD structured data, and related-article
// cards. Add new articles here first, then create the page component.
import problemThumb from 'assets/images/article_bubbles/article-thumb-the-problem-with-esports_9.6.26.webp';
import fixItThumb from 'assets/images/article_bubbles/article-thumb-how-we-fix-it_9.6.26.webp';
import provideThumb from 'assets/images/article_bubbles/article-thumb-what-we-provide_9.6.26.webp';
import joinLeagueThumb from 'assets/images/article_bubbles/article-thumb-how-to-join-esports-league_9.6.26.webp';
import esportsBetterThumb from 'assets/images/article_bubbles/article-thumb-how-esports-can-be-better_9.6.26.webp';
import uniqueThumb from 'assets/images/article_bubbles/article-thumb-why-usync-is-unique_9.6.26.webp';
import bo7Thumb from 'assets/images/article_bubbles/article-thumb-bo7-controller-settings_9.6.26.webp';
import dpiThumb from 'assets/images/article_bubbles/article-thumb-best-dpi-valorant_9.6.26.webp';
import ps5Thumb from 'assets/images/article_bubbles/article-thumb-enable-120hz-ps5_9.6.26.webp';
import tournamentsNearYouThumb from 'assets/images/article_bubbles/article-thumb-esports-tournaments-near-you_9.6.26.webp';
import joinTournamentThumb from 'assets/images/article_bubbles/article-thumb-join-online-gaming-tournament_9.6.26.webp';

import problemHero from 'assets/images/article_specific/article-hero-the-problem-with-esports_9.6.26.webp';
import fixItHero from 'assets/images/article_specific/article-hero-how-we-fix-it_9.6.26.webp';
import provideHero from 'assets/images/article_specific/article-hero-what-we-provide_9.6.26.webp';
import joinLeagueHero from 'assets/images/article_specific/article-hero-how-to-join-esports-league_9.6.26.webp';
import esportsBetterHero from 'assets/images/article_specific/article-hero-how-esports-can-be-better_9.6.26.webp';
import uniqueAndTournamentsHero from 'assets/images/article_specific/article-hero-why-usync-unique-and-tournaments-near-you_9.6.26.webp';
import bo7Hero from 'assets/images/article_specific/article-hero-bo7-controller-settings_9.6.26.webp';
import dpiHero from 'assets/images/article_specific/article-hero-best-dpi-valorant_9.6.26.webp';
import ps5Hero from 'assets/images/article_specific/article-hero-enable-120hz-ps5_9.6.26.webp';
import joinTournamentHero from 'assets/images/article_specific/article-hero-join-online-gaming-tournament_9.6.26.webp';

export const articles = {
    problemWithEsports: {
        slug: "The-Problem-With-Esports",
        name: "The Problem With Esports",
        category: "Industry",
        title: "The Problem With Esports (And How to Fix It)",
        headline: "The Problem With Esports: Fragmentation Is Holding Competitive Gaming Back",
        dek: "Esports is a billion-dollar industry without a front door. Players, teams, and organizers are scattered across dozens of platforms, and it's slowing everyone down.",
        description: "The current problem with esports today and what uSync.gg is doing to combat this major issue plaguing the industry.",
        image: problemHero,
        imageAlt: "Esports arena filled with fans watching a competitive gaming match",
        imageCredit: "World Economic Forum",
        thumb: problemThumb,
        datePublished: "2026-01-28",
        dateModified: "2026-07-11",
        readTime: 4,
        keywords: ["esports fragmentation", "how to join an esports tournament", "esports platform", "LANs near me", "esports community"],
    },

    howWeFixIt: {
        slug: "How-We-Fix-It",
        name: "How We Fix It",
        category: "Platform",
        title: "How uSync Fixes Esports Fragmentation",
        headline: "How uSync Fixes the Problem With Esports",
        dek: "One platform for leagues, LANs, tournaments, and wagers: here's how uSync brings a fragmented industry under one roof.",
        description: "uSync is unifying the entire esports landscape. Find out how you can be a part of this ever growing opportunity.",
        image: fixItHero,
        imageAlt: "Competitive esports players at a live tournament event",
        imageCredit: "World Economic Forum",
        thumb: fixItThumb,
        datePublished: "2026-01-28",
        dateModified: "2026-07-11",
        readTime: 4,
        keywords: ["centralized esports hub", "find esports tournaments", "local esports tournaments", "esports leagues", "LAN events"],
    },

    whatWeProvide: {
        slug: "What-We-Provide",
        name: "What We Provide",
        category: "Platform",
        title: "What uSync Provides: Every Esports Event in One Hub",
        headline: "What uSync Provides for Esports",
        dek: "Tournaments, LANs, leagues, wagers, and verified hosts: a breakdown of everything you'll find on uSync.",
        description: "uSync is a one-stop hub for all esports events across the globe. Find tournaments, LANs, leagues, and much more all in one place.",
        image: provideHero,
        imageAlt: "Esports gaming setup with tournament players competing on PCs",
        imageCredit: "ViewSonic",
        thumb: provideThumb,
        datePublished: "2026-01-28",
        dateModified: "2026-07-11",
        readTime: 4,
        keywords: ["esports hub", "verified esports hosts", "esports tournaments", "LAN tournaments", "esports wagers"],
    },

    joiningEsportsLeague: {
        slug: "How-to-Join-an-Esports-League",
        name: "How to Join an Esports League",
        category: "Guide",
        title: "How to Join an Esports League: Complete Guide",
        headline: "How to Join an Esports League: A Complete Guide",
        dek: "Step-by-step instructions for joining competitive leagues in Call of Duty, Rocket League, Valorant, Warzone, CS2, Halo Infinite, and League of Legends.",
        description: "Learn how to join an esports league, where to find them, and what to do once you're in one. Find your next esports league today.",
        image: joinLeagueHero,
        imageAlt: "Esports team competing in a professional league match on stage",
        imageCredit: "Game Rant",
        thumb: joinLeagueThumb,
        datePublished: "2026-01-28",
        dateModified: "2026-07-11",
        readTime: 7,
        keywords: ["how to join an esports league", "COD leagues", "Valorant leagues", "Rocket League leagues", "CS2 leagues", "Halo Infinite leagues", "League of Legends leagues"],
    },

    howEsportsCanBeBetter: {
        slug: "How-Esports-Can-Be-Better",
        name: "How Esports Can Be Better",
        category: "Industry",
        title: "How Esports Can Be Better",
        headline: "How Esports Can Be Better: Transforming the Community and the Industry",
        dek: "Competitive gaming is bigger than ever, but barriers still hold the community back. A centralized platform can change that.",
        description: "How can esports be better? Centralizing a hub for all of esports to unify the community one step at a time.",
        image: esportsBetterHero,
        imageAlt: "Live esports broadcast production at a major gaming event",
        imageCredit: "Live Production",
        thumb: esportsBetterThumb,
        datePublished: "2026-01-28",
        dateModified: "2026-07-11",
        readTime: 4,
        keywords: ["how esports can be better", "esports community", "centralized esports platform", "esports events", "competitive gaming"],
    },
    whyUsyncIsUnique: {
        slug: "why-usync-is-unique",
        name: "Why uSync Is Like No Other Esports Platform",
        category: "Platform",
        title: "Why uSync Is Like No Other Esports Platform",
        headline: "Why uSync Is Like No Other Esports Platform",
        dek: "uSync isn't your standard esports website. It's an aggregator that brings leagues, LANs, tournaments, wagers, and more into one easy-to-use hub.",
        description: "uSync isn't your standard esports website. It's an aggregator that brings leagues, LANs, tournaments, wagers, and more into one easy-to-use hub.",
        image: uniqueAndTournamentsHero,
        imageAlt: "uSync esports hub displayed on screen at a gaming event",
        imageCredit: "uSync",
        thumb: uniqueThumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 3,
        keywords: ["esports aggregator", "esports platform", "online tournaments", "online leagues", "esports event marketing"],
    },

    blackOps7Settings: {
        slug: "black-ops-7-best-controller-settings",
        name: "Best Black Ops 7 Controller Settings",
        category: "Guide",
        title: "Best Black Ops 7 Controller Settings for Better Aim",
        headline: "Best Black Ops 7 Controller Settings for Better Aim and Movement",
        dek: "Start with the right BO7 controller baseline: sensitivity, deadzone, aim assist, and layout settings to improve your aim and movement immediately.",
        description: "Start with these Black Ops 7 controller settings for faster movement, cleaner aim, and less stick drift on PS5, Xbox, or PC.",
        image: bo7Hero,
        imageAlt: "Call of Duty Black Ops 7 gameplay with controller settings menu",
        thumb: bo7Thumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 6,
        keywords: ["Black Ops 7 controller settings", "BO7 sensitivity", "BO7 deadzone", "aim assist settings", "Call of Duty settings"],
    },

    valorantDPI: {
        slug: "best-dpi-valorant-sensitivity",
        name: "Best DPI for VALORANT",
        category: "Guide",
        title: "Best DPI for VALORANT and How to Find Your Sensitivity",
        headline: "Best DPI for VALORANT and How to Find Your Sensitivity",
        dek: "Start with 800 DPI and 0.25–0.40 in-game sensitivity. Learn how to calculate eDPI and tune your setup with a simple repeatable test.",
        description: "Learn the best starting DPI for VALORANT, calculate eDPI, and tune your sensitivity with a simple repeatable test.",
        image: dpiHero,
        imageAlt: "VALORANT gameplay showing precise crosshair placement",
        thumb: dpiThumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 6,
        keywords: ["best DPI for VALORANT", "VALORANT sensitivity", "eDPI calculator", "VALORANT eDPI", "mouse settings FPS"],
    },

    ps5120Hz: {
        slug: "how-to-enable-120hz-ps5",
        name: "How to Enable 120Hz on PS5",
        category: "Guide",
        title: "How to Enable 120Hz on PS5 and Fix 120 FPS Problems",
        headline: "How to Enable 120Hz on PS5 and Fix 120 FPS Problems",
        dek: "Step-by-step: connect via HDMI, enable 120Hz output, choose performance mode, enable in-game settings, and troubleshoot if PS5 stays at 60Hz.",
        description: "Enable 120Hz on PS5 in minutes. Follow the exact console, display, cable, and in-game steps, plus fixes when PS5 stays at 60Hz.",
        image: ps5Hero,
        imageAlt: "PS5 console connected to a 120Hz gaming display",
        thumb: ps5Thumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 5,
        keywords: ["PS5 120Hz", "enable 120 FPS PS5", "PS5 stuck at 60Hz", "HDMI 2.1 PS5", "PS5 performance mode"],
    },

    esportsTournamentsNearMe: {
        slug: "esports-tournaments-near-me",
        name: "How to Find Esports Tournaments Near You",
        category: "Guide",
        title: "How to Find Esports Tournaments Near You",
        headline: "How to Find Esports Tournaments Near You",
        dek: "Search by game and location on uSync, check local venues, verify eligibility, confirm the organizer, and prepare for event day.",
        description: "Find local esports tournaments, gaming LANs, and amateur competitions near you. Learn where to search and what to check before registering.",
        image: uniqueAndTournamentsHero,
        imageAlt: "Players competing at a local esports tournament venue",
        thumb: tournamentsNearYouThumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 6,
        keywords: ["esports tournaments near me", "local gaming tournaments", "LAN events near me", "amateur esports", "find esports events"],
    },

    joinOnlineTournament: {
        slug: "how-to-join-online-gaming-tournaments",
        name: "How to Join an Online Gaming Tournament",
        category: "Guide",
        title: "How to Join an Online Gaming Tournament",
        headline: "How to Join an Online Gaming Tournament: 7 Steps",
        dek: "Choose your game and format, find a reputable event on uSync, read the rules, register, prepare your connection, check in early, and play.",
        description: "Join your first online gaming tournament with confidence. Find an event, check eligibility, register, prepare, and complete match reporting.",
        image: joinTournamentHero,
        imageAlt: "Gamer competing in an online tournament from a home setup",
        thumb: joinTournamentThumb,
        datePublished: "2026-07-11",
        dateModified: "2026-07-11",
        readTime: 6,
        keywords: ["how to join online gaming tournaments", "online esports tournaments", "tournament check-in", "tournament registration", "beginner esports tournaments"],
    },
};

export const articleList = Object.values(articles);
